// The age-gate sync read on mount must hydrate state to avoid a flash.
// The eslint-react v6 "set-state-in-effect" rule flags this as a false
// positive — see CODE_QUALITY_NOTES.md.
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, AlertTriangle, Check } from "lucide-react";

/**
 * Age Verification gate — session-only persistence via sessionStorage.
 *
 * Uses sessionStorage so acceptance is scoped to the current browser session:
 * the gate reappears whenever a user opens a fresh browser/tab context,
 * matching the session-only UX requirement.
 *
 * State is initialized SYNCHRONOUSLY from sessionStorage to avoid a flash
 * of protected content before the modal mounts.
 */

const AGE_KEY = "hexxium_age_verified";

function readAgeGate() {
  try {
    return sessionStorage.getItem(AGE_KEY) === "1";
  } catch {
    // sessionStorage may be unavailable in private-mode browsers.
    return false;
  }
}

export default function AgeVerification() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  // Initialize synchronously so the gate never flashes in/out.
  // Skip immediately on admin routes — admins have their own auth gate.
  const [show, setShow] = useState(() => !isAdmin && !readAgeGate());
  const [declined, setDeclined] = useState(false);
  const [ageChecked, setAgeChecked] = useState(false);
  const [researcherChecked, setResearcherChecked] = useState(false);
  const bothChecked = ageChecked && researcherChecked;

  // Re-evaluate when the pathname changes (e.g., leaving /admin).
  useEffect(() => {
    if (isAdmin) {
      setShow(false);
      return;
    }
    setShow(!readAgeGate());
  }, [isAdmin]);

  const handleConfirm = () => {
    try {
      sessionStorage.setItem(AGE_KEY, "1");
    } catch {
      // Fail open — if sessionStorage throws, at least hide the modal for this session.
    }
    setShow(false);
    setDeclined(false);
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
        data-testid="age-verification-overlay"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-lg bg-[#0F1115] border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
          data-testid="age-verification-modal"
        >
          {/* Accent line */}
          <div className="h-[3px] bg-gradient-to-r from-[#00AEEF] via-[#00AEEF]/60 to-transparent" />

          <div className="p-7 sm:p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-xl bg-[#00AEEF]/10 border border-[#00AEEF]/20 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-7 h-7 text-[#00AEEF]" />
              </div>
              <p className="text-[10px] uppercase tracking-[3px] text-[#00AEEF] font-semibold mb-1.5">Access Verification</p>
              <h2 className="text-xl font-bold text-white">Research Use Only</h2>
              <p className="text-slate-400 text-[13px] mt-3 leading-relaxed">
                Hexxium Research is a research compound supplier for{" "}
                <span className="text-white font-medium">in vitro laboratory research use only</span> — not for human or
                veterinary consumption. By entering, you confirm that you are at least{" "}
                <span className="text-white font-medium">21 years of age</span> and agree to our{" "}
                <a href="/terms" className="text-[#00AEEF] hover:underline">Terms &amp; Conditions</a>.
              </p>
            </div>

            {declined && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-3 mb-4"
                data-testid="age-declined-warning"
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Both confirmations are required to access this site.
              </motion.div>
            )}

            <div className="space-y-2.5 mb-6">
              <label
                className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors select-none ${
                  ageChecked ? "border-[#00AEEF]/50 bg-[#00AEEF]/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-white/20"
                }`}
                data-testid="age-checkbox-label"
              >
                <input
                  type="checkbox"
                  checked={ageChecked}
                  onChange={(e) => { setAgeChecked(e.target.checked); setDeclined(false); }}
                  className="sr-only"
                  data-testid="age-checkbox"
                />
                <span
                  className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors ${
                    ageChecked ? "bg-[#00AEEF] border-[#00AEEF]" : "border-white/25 bg-transparent"
                  }`}
                >
                  {ageChecked && <Check className="w-3.5 h-3.5 text-white" />}
                </span>
                <span className="text-[13px] text-slate-300 leading-relaxed">
                  I confirm that I am <span className="text-white font-semibold">21 years of age or older</span>.
                </span>
              </label>

              <label
                className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors select-none ${
                  researcherChecked ? "border-[#00AEEF]/50 bg-[#00AEEF]/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-white/20"
                }`}
                data-testid="researcher-checkbox-label"
              >
                <input
                  type="checkbox"
                  checked={researcherChecked}
                  onChange={(e) => { setResearcherChecked(e.target.checked); setDeclined(false); }}
                  className="sr-only"
                  data-testid="researcher-checkbox"
                />
                <span
                  className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-colors ${
                    researcherChecked ? "bg-[#00AEEF] border-[#00AEEF]" : "border-white/25 bg-transparent"
                  }`}
                >
                  {researcherChecked && <Check className="w-3.5 h-3.5 text-white" />}
                </span>
                <span className="text-[13px] text-slate-300 leading-relaxed">
                  I am a <span className="text-white font-semibold">qualified researcher</span> purchasing for{" "}
                  <span className="text-white font-semibold">in vitro laboratory research only</span> — not for human or
                  veterinary use.
                </span>
              </label>
            </div>

            <button
              onClick={() => (bothChecked ? handleConfirm() : setDeclined(true))}
              className={`w-full py-3.5 rounded-xl font-semibold transition-all ${
                bothChecked
                  ? "bg-[#00AEEF] hover:bg-[#00AEEF]/90 text-white shadow-lg shadow-[#00AEEF]/20"
                  : "bg-white/5 text-slate-500 border border-white/10 cursor-not-allowed"
              }`}
              data-testid="age-confirm-btn"
            >
              Enter Site
            </button>

            <p className="text-slate-600 text-[10px] text-center mt-5 leading-relaxed">
              By proceeding, you affirm the statements above are true. Products are not for human or veterinary use,
              not for use in diagnostic procedures, and have not been evaluated by the U.S. Food and Drug Administration.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
